import { useMemo } from 'react';
import {
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanBorrowersMutation } from '@/apis/extended/loanApplicationApi';
import {
  applicationHasCoBorrower,
  borrowersWithoutCoBorrower,
} from '@/utils/loanApplicationBorrower';
import type {
  BorrowerRole,
  LoanApplication,
  LoanBorrower,
} from '@/types/LoanApplicationType';
import { BorrowerEntityFields } from './BorrowerEntityFields';
import {
  MARITAL_STATUS_OPTIONS,
  OWNED_RENTED_YEARS,
  OWNERSHIP_OPTIONS,
} from './constants';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

const BORROWER_TYPES = [
  'Individual',
  'Entity',
  'Trust',
  'Partnership',
] as const;

const blankBorrower = (role: BorrowerRole): LoanBorrower => ({
  role,
  hasEntityTitle: false,
  entityTitle: '',
  entityType: '',
  entityName: '',
  entityEin: '',
  entityState: '',
  entityOwnerCount: null,
  entityOwnerPercentages: [],
  entityCreatedDate: '',
  entityPurpose: '',
  borrowerType: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  ssn: '',
  phone: '',
  dob: '',
  presentAddress: '',
  mailingAddress: '',
  ownership: '',
  ownedRentedYears: '',
  formerAddress: '',
  formerOwnership: '',
  formerOwnedRentedYears: '',
  maritalStatus: '',
  dependentsCount: null,
  citizenship: '',
  alternatePhone: '',
});

type Props = {
  application: LoanApplication;
  which: 'primary' | 'coBorrower';
  readOnly?: boolean;
  /** When false (e.g. standard purchase flow), primary borrower entity toggle is hidden */
  showEntityQuestion?: boolean;
  /** After a co-borrower row is created (e.g. navigate to co-borrower section) */
  onCoBorrowerAdded?: () => void;
  /** After co-borrower is removed from the application */
  onCoBorrowerRemoved?: () => void;
};

function mergeBorrowers(
  list: LoanBorrower[],
  updated: LoanBorrower
): LoanBorrower[] {
  const role = updated.role;
  const rest = list.filter((b) => b.role !== role);
  return [...rest, updated].sort((a, b) =>
    a.role === 'primary' ? -1 : b.role === 'primary' ? 1 : 0
  );
}

export function BorrowerSection({
  application,
  which,
  readOnly,
  showEntityQuestion = true,
  onCoBorrowerAdded,
  onCoBorrowerRemoved,
}: Props) {
  const role: BorrowerRole = which === 'primary' ? 'primary' : 'coBorrower';
  const [patch, { isLoading }] = usePatchLoanBorrowersMutation();
  const hasCoBorrower = applicationHasCoBorrower(application);

  const sourceBorrower = useMemo(() => {
    const found = application.borrowers?.find((b) => b.role === role);
    const base = found
      ? { ...blankBorrower(role), ...found, role }
      : blankBorrower(role);
    if (!base.entityName && base.entityTitle) {
      base.entityName = base.entityTitle;
    }
    if (!Array.isArray(base.entityOwnerPercentages)) {
      base.entityOwnerPercentages = [];
    }
    return base;
  }, [application.borrowers, application.updatedAt, role]);

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: `borrower-${which}`,
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: sourceBorrower,
    readOnly,
  });

  const showEntityFields =
    local.borrowerType === 'Entity' ||
    (showEntityQuestion && role === 'primary' && local.hasEntityTitle);
  const showFormer = local.ownedRentedYears === OWNED_RENTED_YEARS[0];

  const saveSection = async () => {
    if (formReadOnly) return;
    const payload = {
      ...local,
      entityTitle: local.entityName || local.entityTitle || '',
    };
    const merged = mergeBorrowers(application.borrowers || [], payload);
    try {
      await patch({
        id: application._id,
        borrowers: merged,
      }).unwrap();
      markSaved(local);
      toast.success('Saved');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const addCoBorrower = async () => {
    if (formReadOnly || hasCoBorrower) return;
    const merged = mergeBorrowers(
      application.borrowers || [],
      blankBorrower('coBorrower')
    );
    try {
      await patch({
        id: application._id,
        borrowers: merged,
      }).unwrap();
      toast.success('Co-borrower added');
      onCoBorrowerAdded?.();
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const removeCoBorrower = async () => {
    if (formReadOnly || !hasCoBorrower) return;
    if (
      !window.confirm(
        'Remove this co-borrower? Any information entered will be deleted.'
      )
    ) {
      return;
    }
    const remaining = borrowersWithoutCoBorrower(application.borrowers);
    try {
      await patch({
        id: application._id,
        borrowers: remaining,
      }).unwrap();
      toast.success('Co-borrower removed');
      onCoBorrowerRemoved?.();
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const updateField = <K extends keyof LoanBorrower>(
    key: K,
    value: LoanBorrower[K]
  ) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const patchLocal = (partial: Partial<LoanBorrower>) => {
    setLocal((prev) => ({ ...prev, ...partial }));
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {which === 'coBorrower' ? 'Co-borrower' : 'Borrower'} details.
        </Typography>
        {which === 'primary' && !hasCoBorrower && !formReadOnly && (
          <Button
            size="small"
            variant="outlined"
            disabled={isLoading}
            onClick={() => void addCoBorrower()}
          >
            Add a co-borrower
          </Button>
        )}
        {which === 'coBorrower' && hasCoBorrower && !formReadOnly && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={isLoading}
            onClick={() => void removeCoBorrower()}
          >
            Remove co-borrower
          </Button>
        )}
      </Stack>
      <Grid container spacing={2}>
        {role === 'primary' && showEntityQuestion && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={local.hasEntityTitle}
                  disabled={disabled}
                  onChange={(_, v) =>
                    setLocal((prev) => ({
                      ...prev,
                      hasEntityTitle: v,
                      borrowerType:
                        v && !prev.borrowerType ? 'Entity' : prev.borrowerType,
                    }))
                  }
                />
              }
              label="Taking title in an entity name"
            />
          </Grid>
        )}
        {/* A personal purchase or refinance is always in your own name;
            entity / trust / partnership only applies to investor loans. */}
        {showEntityQuestion && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Borrower type"
              value={local.borrowerType}
              disabled={disabled}
              onChange={(e) => {
                const next = e.target.value;
                setLocal((prev) => ({
                  ...prev,
                  borrowerType: next,
                  hasEntityTitle:
                    next === 'Entity' ? true : prev.hasEntityTitle,
                }));
              }}
            >
              <MenuItem value="">Select…</MenuItem>
              {BORROWER_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {showEntityFields && (
          <BorrowerEntityFields
            local={local}
            disabled={disabled}
            onPatch={patchLocal}
          />
        )}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="First name"
            value={local.firstName}
            disabled={disabled}
            onChange={(e) => updateField('firstName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Middle name"
            value={local.middleName}
            disabled={disabled}
            onChange={(e) => updateField('middleName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Last name"
            value={local.lastName}
            disabled={disabled}
            onChange={(e) => updateField('lastName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={local.email}
            disabled={disabled}
            onChange={(e) => updateField('email', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="SSN"
            value={local.ssn}
            disabled={disabled}
            onChange={(e) => updateField('ssn', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone"
            value={local.phone}
            disabled={disabled}
            onChange={(e) => updateField('phone', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date of birth"
            value={local.dob}
            disabled={disabled}
            onChange={(e) => updateField('dob', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Present address"
            value={local.presentAddress}
            disabled={disabled}
            onChange={(e) => updateField('presentAddress', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Mailing address"
            value={local.mailingAddress}
            disabled={disabled}
            onChange={(e) => updateField('mailingAddress', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Ownership"
            value={local.ownership}
            disabled={disabled}
            onChange={(e) => updateField('ownership', e.target.value)}
          >
            <MenuItem value="">Select…</MenuItem>
            {OWNERSHIP_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Owned / rented years"
            value={local.ownedRentedYears}
            disabled={disabled}
            onChange={(e) => updateField('ownedRentedYears', e.target.value)}
          >
            <MenuItem value="">Select…</MenuItem>
            {OWNED_RENTED_YEARS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select
            label="Marital status"
            value={local.maritalStatus}
            disabled={disabled}
            onChange={(e) => updateField('maritalStatus', e.target.value)}
          >
            <MenuItem value="">Select…</MenuItem>
            {MARITAL_STATUS_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Dependents (#)"
            type="number"
            value={local.dependentsCount ?? ''}
            disabled={disabled}
            onChange={(e) =>
              updateField(
                'dependentsCount',
                e.target.value === '' ? null : Number(e.target.value)
              )
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Citizenship"
            value={local.citizenship ?? ''}
            disabled={disabled}
            onChange={(e) => updateField('citizenship', e.target.value)}
            placeholder="e.g. US Citizen, Permanent Resident"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Alternate phone"
            value={local.alternatePhone ?? ''}
            disabled={disabled}
            onChange={(e) => updateField('alternatePhone', e.target.value)}
          />
        </Grid>
        {showFormer && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Former address"
                value={local.formerAddress}
                disabled={disabled}
                onChange={(e) => updateField('formerAddress', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Former ownership"
                value={local.formerOwnership}
                disabled={disabled}
                onChange={(e) => updateField('formerOwnership', e.target.value)}
              >
                <MenuItem value="">Select…</MenuItem>
                {OWNERSHIP_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Former owned/rented years"
                value={local.formerOwnedRentedYears}
                disabled={disabled}
                onChange={(e) =>
                  updateField('formerOwnedRentedYears', e.target.value)
                }
              >
                <MenuItem value="">Select…</MenuItem>
                {OWNED_RENTED_YEARS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        )}
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save borrower"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
